import { useEffect, useState } from "react";
import axios from "axios";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";

/**
 * POST P (opdracht Frank 1 november 2026): het blok Persona onderaan Instellingen > Merk.
 *
 * Twee velden (EN en NL) met de tekst uit persona.md, de schakelaar Kosmische laag en de Woordkeus (het bestaande
 * veld van Merk, voorgevuld met [woorden]). De naam NovaMund in de eerste zin en de laatste regel zijn vast: ze
 * staan als grijze regels met een slotje en het veld bevat de laatste regel niet. De backend keurt dezelfde regels
 * nog een keer (PUT /api/branding/persona); dit scherm blokkeert alleen eerder.
 *
 * Een kijker ziet dit blok niet; een lid ziet het zonder te kunnen wijzigen.
 */

/** De tekst zonder de laatste regel. PUUR. */
export function romp(tekst, openingsregel) {
  const delen = (tekst || "").trim().split(/\n\s*\n/).map((a) => a.trim()).filter(Boolean);
  if (delen.length && delen[delen.length - 1] === openingsregel) delen.pop();
  return delen.join("\n\n");
}

/** Woorden in het veld (de laatste regel staat er niet in). PUUR. */
export function telWoorden(tekst) {
  return ((tekst || "").match(/\S+/g) || []).length;
}

/** Staat de naam in de eerste zin? PUUR. */
export function naamInEersteZin(tekst, naam) {
  const eerste = (tekst || "").trim().split(/(?<=[.!?])\s/)[0] || "";
  return eerste.includes(naam);
}

export default function PersonaBlok({ rol }) {
  const { t } = useTaal();
  const [stand, setStand] = useState(null);
  const [en, setEn] = useState("");
  const [nl, setNl] = useState("");
  const [kosmisch, setKosmisch] = useState(true);
  const [woordkeus, setWoordkeus] = useState("");
  const [bezig, setBezig] = useState(false);
  const magWijzigen = rol === "eigenaar";

  useEffect(() => {
    if (!rol || rol === "kijker") return;
    axios.get(`${API}/branding/persona`).then(({ data }) => {
      if (!data || !data.openingsregel) return;
      setStand(data);
      setEn(romp(data.persona_en, data.openingsregel));
      setNl(romp(data.persona_nl, data.openingsregel));
      setKosmisch(data.kosmisch);
      setWoordkeus(data.woordkeus || "");
    }).catch(() => setStand(null));
  }, [rol]);

  if (!rol || rol === "kijker" || !stand) return null;

  const velden = [
    { taal: "en", label: t("Persona EN"), waarde: en, zet: setEn },
    { taal: "nl", label: t("Persona NL"), waarde: nl, zet: setNl },
  ].map((v) => {
    const n = telWoorden(v.waarde);
    return { ...v, n, teLang: n > stand.max_woorden, zonderNaam: !naamInEersteZin(v.waarde, stand.naam) };
  });
  const geblokkeerd = !magWijzigen || bezig || velden.some((v) => v.teLang || v.zonderNaam || !v.waarde.trim());

  const herstel = () => {
    setEn(romp(stand.standaard_en, stand.openingsregel));
    setNl(romp(stand.standaard_nl, stand.openingsregel));
  };

  const opslaan = async () => {
    setBezig(true);
    try {
      const vol = (body) => `${body.trim()}\n\n${stand.openingsregel}`;
      const { data } = await axios.put(`${API}/branding/persona`,
        { persona_en: vol(en), persona_nl: vol(nl), kosmisch, woordkeus });
      setStand(data);
      toast.success(t("Persona opgeslagen"));
    } catch (fout) {
      toast.error(fout?.response?.data?.detail || t("Persona opslaan is mislukt"));
    } finally {
      setBezig(false);
    }
  };

  const slot = (tekst, id) => (
    <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid={id}>
      <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{tekst}</span>
    </p>
  );

  return (
    <div className="space-y-4 rounded-md border p-3" data-testid="persona-blok">
      <div>
        <p className="font-medium">{t("Persona")}</p>
        <p className="text-xs text-muted-foreground">
          {t("Hoe NovaMund klinkt en zichzelf beschrijft. Nooit wat het antwoord inhoudelijk is.")}
        </p>
      </div>
      <fieldset disabled={!magWijzigen} className="space-y-4">
        {slot(t("Naam in de eerste zin (vast): {naam}", { naam: stand.naam }), "persona-slot-naam")}
        {velden.map((v) => (
          <div key={v.taal} className="space-y-1">
            <Label htmlFor={`persona-${v.taal}`}>{v.label}</Label>
            <Textarea id={`persona-${v.taal}`} value={v.waarde} onChange={(e) => v.zet(e.target.value)}
                      className="min-h-[160px] text-sm" data-testid={`persona-${v.taal}`} />
            {slot(stand.openingsregel, `persona-slot-regel-${v.taal}`)}
            <p className={`text-xs ${v.teLang ? "text-red-500 font-medium" : "text-muted-foreground"}`}
               data-testid={`persona-teller-${v.taal}`} data-te-lang={v.teLang ? "ja" : "nee"}>
              {t("{n} van {max} woorden", { n: v.n, max: stand.max_woorden })}
            </p>
            {v.zonderNaam && (
              <p className="text-xs text-red-500" data-testid={`persona-naam-weg-${v.taal}`}>
                {t("De naam {naam} hoort in de eerste zin.", { naam: stand.naam })}
              </p>
            )}
          </div>
        ))}
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label htmlFor="persona-kosmisch">{t("Kosmische laag")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("Uit: de eerste en de laatste alinea gaan niet mee naar het model. De laatste regel blijft.")}
            </p>
          </div>
          <Switch id="persona-kosmisch" checked={kosmisch} onCheckedChange={setKosmisch}
                  data-testid="persona-kosmisch" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="brand-terms">{t("Woordkeus")}</Label>
          <Textarea id="brand-terms" value={woordkeus} onChange={(e) => setWoordkeus(e.target.value)}
                    className="min-h-[60px] text-sm" data-testid="persona-woordkeus" />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={herstel} data-testid="persona-herstel">
            {t("Herstel standaard")}
          </Button>
          <Button type="button" size="sm" onClick={opslaan} disabled={geblokkeerd} data-testid="persona-opslaan">
            {t("Persona opslaan")}
          </Button>
        </div>
      </fieldset>
    </div>
  );
}
